import React, {FC} from "react";
import {Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,} from "@mui/material";
import TimberClassName from "../../timberClass/TimberClassDetail/TimberClassName";
import TimberTypeName from "../../timberType/TimberTypeDetail/TimberTypeName";
import {Shipment} from "../../../types/models/Shipment";
import {PrimaryCordwood} from "../../../../timber/types/models/Cordwood";
import {fixedFormat} from "../../../../utils/numberFormat";
import {styled} from "@mui/material/styles";
import {Trans} from "@lingui/react/macro";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress";
import {useGetCordwoodHistoryByContextQuery} from "../../../apis/CordwoodHistory.api.ts";

const SmallTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const SmallTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const BigTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

const BigTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

type IFormSelectProps = {
    shipment: Shipment;
}

const CordwoodListItemForHistory: FC<IFormSelectProps> = ({shipment}) => {

    const {page, pageSize} = {page: 0, pageSize: 100000}

    const { data: list = {
        items: [],
        totalItems: 0,
        page: 0,
        pageSize: 12,
        numPages: 0
    },
        isLoading: listLoading,
        isFetching: listFetching,
    } = useGetCordwoodHistoryByContextQuery({
        list: {
            page,
            pageSize,
        },
        filter: {
            cuttingId: shipment.cuttingId,
            action: 'shipment/cancel',
            contextHistory: shipment.id,
        }
    })

    if (listLoading || listFetching) {
        return <GradientCircularProgress/>
    }

    return (
    <Box>
        <Paper>
            <Paper>
                <TableContainer>
                    <Table stickyHeader aria-label="simple table" size="small">
                        <TableHead>
                            <SmallTableHeaderRow>
                                <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                <TableCell component="th" align="left"><Trans>Timber Key</Trans></TableCell>
                                <TableCell component="th" align="right"><Trans>Volume (m&#xB3;)</Trans></TableCell>
                                <TableCell component="th" align="left"><Trans>Timber Type</Trans></TableCell>
                            </SmallTableHeaderRow>
                            <BigTableHeaderRow>
                                <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Timber Key</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Volume</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Timber Type</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Class</Trans></TableCell>
                            </BigTableHeaderRow>
                        </TableHead>
                        <TableBody>
                            { list.items.map((cordwood, index) => (
                                <React.Fragment key={ PrimaryCordwood.fromEntity(cordwood).toPath() }>
                                    <SmallTableRow
                                        hover={true}
                                        key={ PrimaryCordwood.fromEntity(cordwood).toPath() + '-small' }
                                    >
                                        <TableCell scope="row" align="center">
                                            { (page * pageSize) + index + 1 }.
                                        </TableCell>
                                        <TableCell scope="row">
                                            <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                { cordwood.timberKey ?
                                                    <>
                                                        { cordwood.timberKey }
                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>
                                            <Typography component="div" variant="body1">
                                                { cordwood.internalKey &&
                                                    <>
                                                        <Trans>IK</Trans>:&nbsp;
                                                        { cordwood.internalKey }
                                                    </>
                                                }
                                            </Typography>
                                        </TableCell>
                                        <TableCell scope="row" align="right">
                                            <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                { cordwood.volume ?
                                                    <>
                                                        {fixedFormat(cordwood.volume) }
                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>

                                        </TableCell>
                                        <TableCell scope="row">
                                            <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                { cordwood.timberTypeId ?
                                                    <>
                                                        <TimberTypeName id={ cordwood.timberTypeId }/>
                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>
                                            <Typography component="div" variant="body1">
                                                { cordwood.classId &&
                                                    <>
                                                        <Trans>C</Trans>:&nbsp;
                                                        <TimberClassName id={ cordwood.classId }/>
                                                    </>
                                                }
                                            </Typography>
                                        </TableCell>
                                    </SmallTableRow>
                                    <BigTableRow
                                        hover={true}
                                        key={ PrimaryCordwood.fromEntity(cordwood).toPath() }
                                    >
                                        <TableCell width="20%" scope="row" align="center">
                                            { (page * pageSize) + index + 1 }.
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="center">
                                            { cordwood.internalKey }
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="right">
                                            {fixedFormat(cordwood.volume)}
                                        </TableCell>
                                        <TableCell  width="20%" scope="row" align="center">
                                            <TimberTypeName id={cordwood.timberTypeId}/>
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="center">
                                            <TimberClassName id={cordwood.classId}/>
                                        </TableCell>
                                    </BigTableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Paper>
    </Box>
    )
}

export default CordwoodListItemForHistory;
